import Footer from 'components/Footer/Footer';
import HomeHeader from 'components/HomeHeader/HomeHeader';
import { Container, Details, ErrorMessage } from 'nhsuk-react-components';
import { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  showStackTrace?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack: string } | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, errorInfo: { componentStack: string }) {
    this.setState({ errorInfo });
  }

  render() {
    const {
      hasError, error, errorInfo,
    } = this.state;
    const {
      children, fallback, showStackTrace = false,
    } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <>
          <HomeHeader />
          <Container>
            <div>
              <div>
                <div>
                  <div>
                    <h1 className="nhsuk-u-padding-top-6">Sorry, there is a problem with this service</h1>
                    <p>
                      We&apos;re having a technical problem and cannot show this page right now.
                    </p>
                    <p>
                      Please go back to the home page by clicking the link below.
                    </p>
                    <p>
                      If the problem continues, contact the SPI team at
                      {' '}
                      <a href="mailto:england.invitationservicesupport@nhs.net">england.invitationservicesupport@nhs.net</a>
                      .
                    </p>
                    {showStackTrace && (
                      <Details>
                        <Details.Summary>
                          Technical details
                        </Details.Summary>
                        <Details.Text>
                          <div
                            id="error-stacktrace"
                            aria-label="Error technical details"
                            role="region"
                          >
                            {error && (
                              <div>
                                <h3>Error:</h3>
                                {' '}
                                <ErrorMessage>
                                  {' '}
                                  {error.message || 'Unknown error occurred.'}
                                </ErrorMessage>
                              </div>
                            )}
                            <div>
                              <h3>Stack trace</h3>
                              <pre>
                                <code>{error?.stack}</code>
                              </pre>
                            </div>

                            {errorInfo?.componentStack && (
                              <div>
                                <h3>Component stack</h3>
                                <pre>
                                  <code>{errorInfo.componentStack}</code>
                                </pre>
                              </div>
                            )}
                          </div>
                        </Details.Text>
                      </Details>
                    )}
                  </div>
                </div>
              </div>
              <div className="nhsuk-u-padding-top-4 nhsuk-u-padding-bottom-4">
                <a href="/">Back to home</a>
              </div>
            </div>
          </Container>
          <Footer />
        </>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
